import { Component, OnInit } from '@angular/core';
import {DisplayTypes} from "@catui/components/panel/types";
import {Project} from "@catcore/types/api-v2";
import {Subscription} from "rxjs";
import {ProjectService} from "@catcore/services";
import {isNullOrUndefined} from "util";
import {BreadcrumbService} from "@catmodules/widgets/breadcrumb/services/breadcrumb.service";
import {StyleType} from "@catui/types";
import {ModifyNetworkIfComponent} from "@catmodels/network-interfaces/components/modify-network-if/modify-network-if.component";
import {ModifyGgwProfileComponent} from "@catmodels/ggw-profiles/components/modify-ggw-profile/modify-ggw-profile.component";
import {GgwProfile} from "@catmodels/ggw-profiles/types";
import {ModalService} from "@catmodules/widgets/slide-in-modal/services/modal.service";
import {GgwProfilePolicyService} from "@catmodels/ggw-profiles/services/ggw-profile-policy.service";
import {GenericResourceService} from "@catfeatures/services/generic-resource.service";

@Component({
  selector: 'cat-ggw-profiles',
  templateUrl: './ggw-profiles.component.html',
  styleUrls: ['./ggw-profiles.component.scss']
})
export class GgwProfilesComponent implements OnInit {

  tableItems = [];
  dataTableInitialized: boolean;
  project: Project;

  ggwProfiles = [];
  ggwProfilePolicies = [];
  ggwProfileDefaultPolicies = [];
  guiZones = [];

  panelDisplayType = DisplayTypes;

  cols = [];
  staticCols = [
    { field: 'name', header: 'Profile Name', parentShortName: 'Zones', static: true },
    { field: 'unitLabel', header: 'Unit', parentShortName: 'Zones', static: true },
    { field: 'unitZone', header: 'Zone', parentShortName: 'Zones', static: true },
    { field: 'type', header: 'Type', parentShortName: 'Zones', static: true },
    { field: 'defaultPolicy', header: "Default Policy", parentShortName: 'Zones', static: true },
  ];

  groupCols = [];
  staticGroupCols = [
    { shortName: 'Zones', count: this.staticCols.length, children: this.staticCols }
  ];

  projectSubscription: Subscription;
  ggwProfileDefaultZonePolicySubscr: Subscription;
  ggwProfileSubscription: Subscription;
  ggwProfilePolicySubscription: Subscription;

  defaultPoliciesReady: boolean;
  profilesReady: boolean;
  policiesReady: boolean;

  styleType = StyleType;

  constructor(private projectService: ProjectService,
              private breadcrumbService: BreadcrumbService,
              private modalService: ModalService,
              private ggwProfilePolicyService: GgwProfilePolicyService,
              private resourceService: GenericResourceService) { }

  ngOnInit() {
    // first get the current project
    this.projectSubscription = this.projectService.getCurrentProject()
      .subscribe((project) => {
        this.project = project;
        const isActiveProject = !isNullOrUndefined(project) && !isNullOrUndefined(project.id);
        if (isActiveProject) {
          this.dataTableInitialized = false;
          this.defaultPoliciesReady = false;
          this.profilesReady = false;
          this.policiesReady = false;

          this.guiZones = [];
          this.guiZones = this.projectService
            .extractGuiZonesFrom(this.project)
            .filter((guiZone) => { return guiZone.type !== 'zoneGroup' });

          this.ggwProfiles = [];
          this.ggwProfileSubscription = this.projectService.getGgwProfiles(this.project)
            .subscribe((ggwProfiles: any) => {
              this.ggwProfiles = ggwProfiles;
              this.profilesReady = true;
              this.updateDataTable();
            });

          this.ggwProfilePolicies = [];
          this.ggwProfilePolicySubscription = this.projectService.getAllGgwProfilePolicies(this.project)
            .subscribe((ggwProfilePolicies: any) => {
              // We are only interested in zone policies here
              this.ggwProfilePolicies = ggwProfilePolicies.filter((policy) => {
                return policy._links.hasOwnProperty("gui_zone");
              });
              this.policiesReady = true;
              this.updateDataTable();
            });

          this.ggwProfileDefaultPolicies = [];
          this.ggwProfileDefaultZonePolicySubscr = this.projectService.getGgwProfileDefaultZonePolicies(this.project)
            .subscribe((defaultPolicies) => {
              this.ggwProfileDefaultPolicies = defaultPolicies;
              this.defaultPoliciesReady = true;
              this.updateDataTable();
            })
        }
      });

    this.breadcrumbService.update([{label: 'Access Profiles', value: 'ggwAccessProfiles' }]);
  }

  ngOnDestroy() {
    this.ggwProfileDefaultZonePolicySubscr.unsubscribe();
    this.projectSubscription.unsubscribe();
    this.ggwProfilePolicySubscription.unsubscribe();
    this.ggwProfileSubscription.unsubscribe();
  }

  /**
   * Returns true if the given column name belongs to a static column.
   *
   * @param colName
   */
  isStaticCol(colName) {
    return this.staticCols.find((c) => c.field === colName);
  }

  /**
   * Returns true if the zone is allowed for this ggw profile.
   *
   * @param rowItem
   * @param colField
   */
  isActive(rowItem, colField) {
    return this.isActiveStatus(this.getStatus(rowItem, colField));
  }

  isActiveEnabled(rowItem, colField) {
    let status = this.getStatus(rowItem, colField);
    return this.isEnabledStatus(status) && this.isActiveStatus(status);
  }

  isActiveRecursive(rowItem, colField) {
    return this.isActiveRecursiveStatus(this.getStatus(rowItem, colField));
  }

  isActiveDisabled(rowItem, colField) {
    return this.getStatus(rowItem, colField) === 'active-disabled';
  }

  isInactiveEnabled(rowItem, colField) {
    return this.getStatus(rowItem, colField) === 'inactive-enabled';
  }

  isEnabledStatus(status) {
    return status === 'active-enabled' || status === 'inactive-enabled' || status === 'active-enabled-recursive';
  }

  isActiveStatus(status) {
    return status === 'active-disabled' || status === 'active-enabled' || status === 'active-enabled-recursive';
  }

  isActiveRecursiveStatus(status) {
    return status === 'active-enabled-recursive';
  }

  isAllowPolicy(policy) {
    return policy === 'allow-recursive' || policy === 'allow';
  }

  isRecursivePolicy(policy) {
    return policy === 'allow-recursive' || policy === 'deny-recursive';
  }

  /**
   * Returns true if the default policy differs from the current policy.
   *
   * @param rowItem
   * @param colField
   */
  hasDifferentDefault(rowItem, colField) {
    const currentPolicy = this.getCurrentPolicy(rowItem, colField);
    const defaultPolicy = rowItem.hasOwnProperty(colField) && rowItem[colField].hasOwnProperty('defaultPolicy') ? rowItem[colField].defaultPolicy : 'none';
    return currentPolicy !== defaultPolicy;
  }

  /**
   * Returns the status for a given cell, identified by rowItem and column field.
   * For seats, the status of the parent zone is checked for a recursive policy.
   *
   * @param rowItem
   * @param colField
   */
  getStatus(rowItem, colField) {
    if (this.isStaticCol(colField)) {
      return null;
    }

    if (rowItem.ggwProfile.default_policy === 'allow') {
      return 'active-disabled';
    }

    const guiZone = this.findGuiZone(colField);
    if (guiZone.type === 'seat') {
      const parent = this.findGuiZone(guiZone._links.zone_parent.href);
      if (this.isActiveRecursiveStatus(this.getStatus(rowItem, guiZone._links.zone_parent.href))) {
        return 'active-disabled';
      }
    }

    if (rowItem[colField] && rowItem[colField].policy === 'allow') {
      return 'active-enabled';
    }

    if (rowItem[colField] && rowItem[colField].policy === 'allow-recursive') {
      return 'active-enabled-recursive';
    }

    if (rowItem[colField] && rowItem[colField].policy === 'none') {
      return 'inactive-enabled';
    }

    if (rowItem[colField] && rowItem[colField].defaultPolicy === 'allow') {
      return 'active-enabled';
    }

    if (rowItem[colField] && rowItem[colField].defaultPolicy === 'allow-recursive') {
      return 'active-enabled-recursive';
    }

    return 'inactive-enabled';
  }

  /**
   * Returns true if the edit button should be displayed for a cell.
   *
   * @param rowItem
   * @param colField
   */
  showEditButton(rowItem, colField) {
    return colField === 'name';
  }

  /**
   * Returns the tooltip title for a cell.
   *
   * @param rowItem
   * @param col
   */
  getTitle(rowItem, col) {
    if (col.static) {
      return col.header;
    }

    let title = '';

    if (this.isEnabledStatus(this.getStatus(rowItem, col.field))) {
      title += 'Click to toggle ' + col.header;
    }

    if (this.hasDifferentDefault(rowItem, col.field)) {
      title += (title === '' ? '' : '. ');
      title += 'Default: ' + this.getDefaultPolicy(rowItem, col.field);
    }

    return title;
  }

  /**
   * Handles a cell click. If a data cell has been clicked, the policy of the given zone/profile combination
   * is toggled through none -> active -> active recursive -> none.
   *
   * Updating the policy means to either add, update or delete a GgwProfilePolicy. The operation depends
   * on the default policy: If the desired policy equals the default policy, no GgwProfilePolicy is needed.
   *
   * @param rowItem
   * @param col
   */
  cellClick(rowItem, col) {
    if (col.static) {
      return;
    }

    const curPolicy = this.getCurrentPolicy(rowItem, col.field);
    const nextPolicy = this.getNextTogglePolicy(curPolicy);

    // If next policy equals default - just remove item
    const defaultPolicy = this.getDefaultPolicy(rowItem, col.field);
    if (defaultPolicy === nextPolicy) {
      this.deleteItem(rowItem[col.field]);
      return;
    }

    // If item exists - update it
    if (rowItem.hasOwnProperty(col.field) && rowItem[col.field].hasOwnProperty('policy')) {
      this.updateItem(rowItem[col.field], nextPolicy);
      return;
    }

    // Else, add new item
    this.addItem(rowItem.ggwProfile._links.self.href, col.field, nextPolicy);
  }

  /**
   * Handles a click on the edit button of a profile.
   *
   * @param rowItem
   * @param colField
   */
  editButtonClick(rowItem, colField) {
    const ggwProfile = rowItem.ggwProfile;
    this.openModalForGgwProfile(ggwProfile);
  }

  /**
   * Find a gui zone by uri.
   *
   * @param guiZoneUri
   */
  private findGuiZone(guiZoneUri) {
    return this.guiZones.find((gz) => { return gz._links.self.href === guiZoneUri });
  }

  getExplicitPolicy(rowItem, colField) {
    if (rowItem.hasOwnProperty(colField)) {
      return rowItem[colField].policy;
    }

    return null;
  }

  /**
   * Returns current policy, which can be determined by an explicit ggw profile policy entity (1st prio)
   * or by an existing defualt policy.
   *
   * @param rowItem
   * @param colField
   */
  private getCurrentPolicy(rowItem, colField) {
    if (!rowItem.hasOwnProperty(colField)) {
      return 'none';
    }

    if (rowItem[colField].hasOwnProperty('policy')) {
      return rowItem[colField].policy;
    }

    if (rowItem[colField].hasOwnProperty('defaultPolicy')) {
      return rowItem[colField].defaultPolicy;
    }

    return 'none';
  }

  /**
   * Returns the next policy for a toggle operation.
   *
   * @param policy
   */
  private getNextTogglePolicy(policy) {
    const next = {
      'none': 'allow',
      'allow': 'allow-recursive',
      'allow-recursive': 'none',
    };
    return next[policy];
  }

  /**
   * Adds a new ggw profile policy.
   *
   * @param ggwProfileUri
   * @param guiZoneUri
   * @param policy
   */
  private addItem(ggwProfileUri, guiZoneUri, policy) {
    this.ggwProfilePolicyService.add({
      ggw_profile: ggwProfileUri,
      gui_zone: guiZoneUri,
      policy: policy
    })
      .subscribe((result) => {
        this.ggwProfilePolicies.push(result);
        this.updateTableItem(ggwProfileUri, guiZoneUri, result);
      });
  }

  /**
   * Updates an existing ggw profile policy.
   *
   * @param item
   * @param policy
   */
  private updateItem(item, policy) {
    const subscr = this.resourceService.findServiceForResource(item._links.self.href)
      .save({ policy: policy })
      .finally(() => subscr.unsubscribe())
      .subscribe((result) => {
        this.updateTableItem(item._links.ggw_profile.href, item._links.gui_zone.href, result);
    });
  }

  /**
   * Deletes a given ggw profile policy.
   *
   * @param item the ggw profile policy
   */
  private deleteItem(item) {
      this.ggwProfilePolicyService.delete(item)
        .subscribe((result) => {
          let index = this.ggwProfilePolicies.indexOf(item);
          this.tableItems
            .filter((tableItem) => {return item._links.ggw_profile.href === tableItem.ggwProfile._links.self.href})
            .forEach((tableItem) => {
              const defaultPolicy = (tableItem[item._links.gui_zone.href].hasOwnProperty('defaultPolicy') ? tableItem[item._links.gui_zone.href].defaultPolicy : null);
              if (tableItem[item._links.gui_zone.href]) {
                delete tableItem[item._links.gui_zone.href];
              }
              if (defaultPolicy) {
                tableItem[item._links.gui_zone.href] = {};
                tableItem[item._links.gui_zone.href]['defaultPolicy'] = defaultPolicy;
              }
            });
          this.ggwProfilePolicies.splice(index, 1);
        });
  }

  private updateDataTable() {
    if (!this.defaultPoliciesReady || !this.policiesReady || !this.profilesReady) {
      return;
    }

    this.updateCols();

    this.tableItems = [];
    this.ggwProfiles.forEach((ggwProfile) => {
      this.addGgwProfile(ggwProfile);
    })
  }

  private updateCols() {
    let dataCols = [];
    this.guiZones.forEach((guiZone) => {
      const col = {
        field: guiZone._links.self.href,
        header: guiZone.short_name,
        parentShortName: this.getParentShortName(guiZone),
        static: false
      };
      dataCols.push(col);
    });
    dataCols.sort((a, b) => {
      let parentZoneCmp = a.parentShortName.toUpperCase().localeCompare(b.parentShortName.toUpperCase());
      if (parentZoneCmp !== 0) {
        return parentZoneCmp;
      }
      return a.header.toUpperCase().localeCompare(b.header.toUpperCase());
    });

    let colspanCount = dataCols.reduce((p, c) => {
      let name = c.parentShortName;
      if (!p.hasOwnProperty(name)) {
        p[name] = {
          count: 0,
          children: []
        };
      }
      p[name].count++;
      p[name].children.push(c);
      return p;
    }, {});

    let dataColspanCount = Object.keys(colspanCount).map(k => {
      return {
        shortName: k,
        count: colspanCount[k].count,
        children: colspanCount[k].children
      };
    });

    this.groupCols = [
      ...this.staticGroupCols,
      ...dataColspanCount
    ];
    this.cols = [
      ...this.staticCols,
      ...dataCols
    ];
  }

  private updateTableItem(ggwProfileUri, guiZoneUri, profilePolicy) {
    this.tableItems
      .filter((item) => {return item.ggwProfile._links.self.href === ggwProfileUri})
      .forEach((item) => {
        const defaultPolicy = (item.hasOwnProperty(guiZoneUri) && item[guiZoneUri].hasOwnProperty('defaultPolicy') ? item[guiZoneUri].defaultPolicy : null);
        item[guiZoneUri] = profilePolicy;
        if (defaultPolicy) {
          item[guiZoneUri]['defaultPolicy'] = defaultPolicy;
        }
      });
  }


  private getParentShortName(guiZone) {
    if (guiZone._embedded && guiZone._embedded.zone_parent) {
      return guiZone._embedded.zone_parent.short_name;
    } else {
      return guiZone.short_name;
    }
  }

  private afterGgwProfileCreate(ggwProfile: GgwProfile) {
    this.ggwProfileDefaultZonePolicySubscr = this.projectService.getGgwProfileDefaultZonePolicies(this.project)
      .subscribe((defaultPolicies) => {
        this.ggwProfileDefaultPolicies = defaultPolicies;
        this.addGgwProfile(ggwProfile);
      });
  }

  private afterGgwProfileUpdate(ggwProfile: GgwProfile) {
    this.tableItems.filter((p) => { return p.ggwProfile.id === ggwProfile.id})
      .forEach((item: GgwProfile) => {
        item['name'] = ggwProfile.name;
        item['defaultPolicy'] = ggwProfile.default_policy;
        item['type'] = ggwProfile.type;
        item['ggwProfile'] = ggwProfile;
      });
  }

  private afterGgwProfileDelete(ggwProfile: GgwProfile) {
    const newItems = [...this.tableItems];
    let index = newItems.findIndex((p) => p.ggwProfile.id === ggwProfile.id);
    if (index >= 0) {
      newItems.splice(index, 1);
      this.tableItems = newItems;
    }

    index = this.ggwProfiles.findIndex((p) => p.id === ggwProfile.id);
    if (index >= 0) {
      this.ggwProfiles.splice(index, 1);
    }
  }

  private addGgwProfile(ggwProfile: GgwProfile) {
    const item = {
      'name': ggwProfile.name,
      'type': ggwProfile.type,
      'unitLabel': ggwProfile._links && ggwProfile._links.unit ? ggwProfile._links.unit.label : '',
      'unitZone': ggwProfile.unit_zone_short_name ? ggwProfile.unit_zone_short_name : '',
      'defaultPolicy': ggwProfile.default_policy,
      'ggwProfile': ggwProfile,
    };

    this.ggwProfilePolicies
      .filter((profilePolicy) => profilePolicy._links.ggw_profile.href === ggwProfile._links.self.href)
      .forEach((profilePolicy) => {
      item[profilePolicy._links.gui_zone.href] = profilePolicy;
    });


    const defaultPolicies = this.ggwProfileDefaultPolicies[ggwProfile._links.self.href];
    Object.keys(defaultPolicies).forEach((zoneUri) => {
      if (!item.hasOwnProperty(zoneUri)) {
        item[zoneUri] = {};
      }
      item[zoneUri]['defaultPolicy'] = defaultPolicies[zoneUri];
    });

    this.tableItems = [...this.tableItems, item];
  }

  private getDefaultPolicy(rowItem, colField) {
    if (rowItem.hasOwnProperty(colField) && rowItem[colField].hasOwnProperty('defaultPolicy')) {
      return rowItem[colField].defaultPolicy;
    }

    return 'none';
  }

  private defaultGgwProfile: GgwProfile = {
    name: null,
    default_policy: null,
    type: null,
    aircraft_schematic_url: null,
    show_in_profile_list: false,
    show_connected_clients: false,
    _links: null
  };

  openModalForGgwProfile(ggwProfile = this.defaultGgwProfile) {
    const curProfile = ggwProfile;
    const compRef = this.modalService.open(ModifyGgwProfileComponent);
    compRef.componentInstance.enableDelete = (ggwProfile.id !== null);
    compRef.componentInstance.project = this.project;
    compRef.componentInstance.ggwProfile = ggwProfile;
    compRef.componentInstance.afterCreate.subscribe((newProfile) => this.afterGgwProfileCreate(newProfile));
    compRef.componentInstance.afterUpdate.subscribe((ggwProfile) => this.afterGgwProfileUpdate(ggwProfile));
    compRef.componentInstance.afterDelete.subscribe(() => this.afterGgwProfileDelete(curProfile));
    return compRef;
  }
}



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_develop@2/app/Resources/Public/src/app/modules/models/ggw-profiles/components/ggw-profiles/ggw-profiles.component.ts